/**
 * Every string the credited-person register renders.
 *
 * The lede defines what the surface holds, and does not open by denying what it
 * is not: an earlier phrasing began «none of them has an account», which raises
 * the question of accounts in order to answer it — a question this surface does
 * not carry. What an operator needs is what they manage here and where its
 * effect shows.
 */
export const copy = {
  documentTitle: 'المنسوبون — وحدة تحكّم شاهدني',
  crumb: 'المنسوبون',

  head: {
    title: 'المنسوبون',
    lede: 'سجلّ مَن يُنسب إليه عملٌ في الفهرس: مخرجون وممثّلون وكتّاب ومصوّرون ومنتجون ومؤلّفو موسيقى. لكلّ واحدٍ منهم صفحةٌ عامّة تحمل اسمه وسيرته وكلّ عنوانٍ يُنسب إليه، ومن هنا تُحرَّر تلك الصفحة.',
  },

  section: {
    heading: 'سجلّ المنسوبين',
    add: 'أضف منسوباً',
    /** Where each column's value is authored — said before an operator looks for it. */
    context: 'افتح اسم المنسوب لتحرير بياناته وأعماله. الأدوار تأتي من أعماله، فلا تُحرَّر من هذا الجدول.',
  },

  caption: 'المنسوبون في الفهرس، وأدوارهم وعدد الأعمال المنسوبة إلى كلٍّ منهم',

  /** The register's own search — served by the route's `term`, folded like every Arabic search here. */
  search: {
    label: 'ابحث في المنسوبين بالاسم',
    placeholder: 'ابحث بالاسم…',
    empty: (term: string) => `لا منسوب يطابق «${term}».`,
  },

  columns: {
    person: 'المنسوب',
    roles: 'الأدوار',
    works: 'الأعمال',
    publicPage: 'الصفحة العامّة',
  },

  count: {
    note: 'منسوباً وصل من الخدمة.',
    /**
     * The ceiling, said only when the register has actually reached it.
     *
     * The route's own bound is a hundred rows, and the register will pass it —
     * so the surface has to be able to say «هؤلاء المئة» rather than let the
     * operator read a full-looking table as the whole register. Stated with the
     * way out, because the same read takes a term.
     */
    ceiling: (limit: string) =>
      `تُعرَض أحدث ${limit} منسوب من السجلّ — وهو أقصى ما تُسلّمه الخدمة في القراءة الواحدة. ابحث بالاسم للوصول إلى من هو خارجها.`,
    /**
     * Said always, because a line that appears only on truncation reads as
     * completeness — and it is still said beside the pager, because the two
     * answer different questions: the pager walks what arrived, this says the
     * route hands over one batch and names no total.
     */
    more: 'البحث يجري في الخدمة على كامل السجلّ، والتنقّل أدناه يقسّم ما وصل.',
  },

  /**
   * The traversal control, and the one in this console that walks rows already
   * in hand: `GET /admin/creditProfiles` answers a bare array with no
   * `page_size`, no `total` and no `has_next`, so there is no page to ask for.
   */
  pager: {
    label: 'صفحات السجلّ',
    shownOne: 'منسوب واحد معروض',
    shownTwo: 'منسوبان معروضان',
    shownFew: 'منسوبين معروضين',
    shownMany: 'منسوباً معروضاً',
  },

  states: {
    loading: 'جارٍ تحميل السجلّ',
    empty: 'لا منسوب في السجلّ بعد. أضف أوّل واحد لتبدأ نسبة الأعمال إليه.',
  },

  dialog: {
    heading: 'أضف منسوباً',
    lede: 'أضف الاسم والسيرة. تُسجَّل أعماله بعد ذلك من صفحته.',
    requiredNote: 'الحقول المعلَّمة بـ',
    requiredNoteTail: ' مطلوبة.',
    name: {
      label: 'اسم العرض',
      required: 'أدخل اسم العرض.',
    },
    biography: {
      label: 'السيرة',
      hint: 'سطران أو ثلاثة تظهر في صفحته العامّة تحت اسمه.',
    },
    commit: 'أضف',
    cancel: 'ألغِ',
  },

  /** Announced politely — the row is already on screen, and this names it. */
  added: (name: string) => `أُضيف ${name} إلى السجلّ.`,
} as const;
